<template>
	<Teleport to="html">
		<BaseModal :show="resourceSectionToEdit != null || resourceSubsectionToEdit != null" border-classes="border-t-6 border-secondary-light">
			<EditResourceSectionModalContent
				v-if="showResourceSection"
				:resource-section-to-edit="resourceSectionToEdit"
				@subsection-clicked="onSubsectionClicked"
				@add-subsection-clicked="onAddSubsectionClicked"
				@section-deleted="onSectionDeleted"
				@dismiss="onEditSectionDismiss" />

			<EditResourceSectionModalContent
				v-if="showResourceSubsection"
				:resource-section-to-edit="resourceSubsectionToEdit"
				:is-subsection="true"
				@resource-clicked="onResourceClicked"
				@add-resource-clicked="onAddResourceClicked"
				@subsection-deleted="onSubsectionDeleted"
				@dismiss="onEditSubsectionDismiss" />

			<EditResourceModalContent
				v-if="showResource"
				:resource-to-edit="resourceToEdit"
				@resource-deleted="onResourceDeleted"
				@dismiss="onEditResourceDismiss" />
		</BaseModal>
	</Teleport>

	<AdminLayout>
		<div class="flex flex-row mb-4">
			<p class="admin-center-subtitle">Resources</p>
			<SubmitButton label="Add Resource Section" @click="onAddSectionClicked" size="small"/>
		</div>

		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
			@on-data-loaded="onDataLoaded"
		>
			<template #name="data">
				<span class="clickable-text" @click="onSectionClicked(data.value)">{{ data.value.name }}</span>
			</template>
		</PortalDataTable>
	</AdminLayout>
</template>

<script setup lang="js">
import AdminLayout from "@/layouts/AdminLayout.vue"
import { computed, ref } from 'vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import EditResourceSectionModalContent from '@/views/Admin/Resources/EditResourceSectionModalContent.vue'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import EditResourceModalContent from '@/views/Admin/Resources/EditResourceModalContent.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const resourceSectionToEdit = ref(null)
const resourceSubsectionToEdit = ref(null)
const resourceToEdit = ref(null)
const defaultOrganizationGroups = ref([])

const showResourceSection = computed(() => {
	return resourceSectionToEdit.value != null && resourceSubsectionToEdit.value == null && resourceToEdit.value == null
})
const showResourceSubsection = computed(() => {
	return resourceSubsectionToEdit.value != null && resourceToEdit.value == null
})
const showResource = computed(() => {
	return resourceToEdit.value != null
})

const toaster = new Toaster();

const columns = [
	{ field: "name", title: "Name", sort: true, search: true },
]

const endpoints = [
	new DataTableEndpointDefinition(
		{
			endpointRoute: 'resources',
			initialSortColumn: 'name',
			searchPlaceholder: 'Search by name',
			columns: columns,
		}
	)
]

const table = ref(null)

function onDataLoaded(records) {
	defaultOrganizationGroups.value = records['default_groups']
}

function onSectionClicked(section) {
	resourceSectionToEdit.value = section
}

function onEditSectionDismiss(modifiedSection) {
	resourceSectionToEdit.value = null

	if (modifiedSection) {
		toaster.success('Resource section updated!')
		doReload()
	}
}

function onEditSubsectionDismiss(modifiedSubsection) {
	if (modifiedSubsection) {
		const subsection = resourceSectionToEdit.value.subcategories.find((subsection) => {
			return subsection.id === modifiedSubsection.id
		})

		if (subsection) {
			subsection.name = modifiedSubsection.name
		}
		else {
			resourceSectionToEdit.value.subcategories.push(modifiedSubsection)
		}

		toaster.success('Resource subsection updated!')
	}

	resourceSubsectionToEdit.value = null
}

function onEditResourceDismiss(modifiedResource) {
	if (modifiedResource) {
		const resource = resourceSubsectionToEdit.value.resources?.find((resource) => {
			return resource.id === modifiedResource.id
		})

		if (resource) {
			resource.name = modifiedResource.name
			resource.description = modifiedResource.description
			resource.url = modifiedResource.url
			resource.groups = modifiedResource.groups
		}
		else {
			if (!resourceSubsectionToEdit.value.resources) {
				resourceSubsectionToEdit.value.resources = []
			}

			resourceSubsectionToEdit.value.resources.push(modifiedResource)
		}

		toaster.success('Resource updated!')
	}

	resourceToEdit.value = null
}

function onSectionDeleted() {
	resourceSectionToEdit.value = null
	doReload()
}

function onSubsectionDeleted(deletedSubsection) {
	resourceSectionToEdit.value.subcategories =
		resourceSectionToEdit.value.subcategories.filter((resource) => { return resource.id !== deletedSubsection.id })

	resourceSubsectionToEdit.value = null
}

function onResourceDeleted(deletedResource) {
	resourceSubsectionToEdit.value.resources =
		resourceSubsectionToEdit.value.resources.filter((resource) => { return resource.id !== deletedResource.id })

	resourceToEdit.value = null
}

function onSubsectionClicked(subsection) {
	subsection.parent = resourceSectionToEdit.value
	subsection.breadcrumbs = buildBreadcrumbs(subsection)
	resourceSubsectionToEdit.value = subsection
}

function onResourceClicked(resource) {
	resource.parent = resourceSubsectionToEdit.value
	resource.breadcrumbs = buildBreadcrumbs(resource)
	resourceToEdit.value = resource
}

function doReload() {
	if (table.value) {
		table.value.loadData()
	} else {
		location.reload()
	}
}

function onAddSectionClicked() {
	resourceSectionToEdit.value = {}
}

function onAddSubsectionClicked() {
	resourceSubsectionToEdit.value = {
		'category_id': resourceSectionToEdit.value.id
	}
}

function onAddResourceClicked() {
	resourceToEdit.value = {
		'subcategory_id': resourceSubsectionToEdit.value.id,
		'groups': defaultOrganizationGroups.value,
	}
}

function buildBreadcrumbs(resourceToEdit) {
	const breadcrumbs = []
	let parent = resourceToEdit.parent

	while (parent) {
		breadcrumbs.push(`<span class="font-semibold">${parent.name}</span>`)
		parent = parent?.parent
	}

	return breadcrumbs.reverse().join('<span class="font-light"> / </span>')
}

</script>
